import { getConfigForMeasuredTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  wrapAsDerivedFromMeasurementsConfig as derivedFrom,
  measuredUsingConfigFor as measuredUsing,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import {
  getConfig as getMoistureContentConfig,
  getMoistureContentPercent,
} from 'Components/Worksheets/MoistureContentWorksheet/BsEn/Config/index';
import {
  ConditionOfSampleTested,
  DryMassSource,
  WetMassSource,
} from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import qestIds from 'Components/Worksheets/QestIds';
import * as oPath from 'Lib/Object/Path/Immutable';
import { getSeparationPath } from '../../Helpers';
import { getDerivedFromMeasurementsConfigForPortionSieving } from '../../derivedFromMeasurementsConfig';
import { getMeasuredUsingConfigForPortionSieving } from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

/**
 * Most masses are formatted based on the readability of the corresponding balance.
 * However, some require more complicated logic to determine which balances apply.
 * For these masses, we're currently keeping it simple and displaying them to a fixed 0.1 g
 * (default readability).
 */
const massConfig = { uom: 'g', format: { nearest: 0.1 } };

const getConfig = (prefix: string) => {
  const dryMassBalanceReadability = getDryMassBalance(prefix);

  const maxSeparations = 2;
  let config = {
    ...getPortionMassesConfig(`${prefix}.WholePortion`, 'Portion', dryMassBalanceReadability),
    ...getPortionSievingConfig(prefix, `${prefix}.WholePortion.Sieving`),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Washing`),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Separation.CoarsePortion.Washing`),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Washing`),

    [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: {
      options: [
        { value: false, label: 'Based on the selected sieve set' },
        { value: true, label: `Based on the sample's specification` },
      ],
    },

    [`${prefix}.Results.D85`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D60`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D50`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D30`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D15`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D10`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.CoefficientOfCurvature`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.CoefficientOfUniformity`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.PercentagePassing_0_063_Sieve`]: { format: { nearest: 0.1 } },
    [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
    [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
    [`${prefix}.Results.EquivalentMassPassing_0_063_Sieve`]: dryMassBalanceReadability,
  };

  Object.assign(
    config,
    ...[...Array(maxSeparations).keys()].map((i) => ({
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.CoarsePortion`,
        'Coarse Portion',
        dryMassBalanceReadability
      ),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.CoarsePortion.Sieving`),
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.FinePortion`,
        'Fine Portion',
        dryMassBalanceReadability
      ),
      //For this test method, the "fine portion moisture content" is attached to the fine portion subspecimen,
      //but it is actually a *different* subspecimen. We avoid the 'Subspecimen' prefix because we don't want
      //users to be confused by it.
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen`,
        'Material',
        dryMassBalanceReadability
      ),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Sieving`),
      //...getPortionWashingConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Washing`),
    }))
  );

  return mergeConfig(config, getLabelConfig(prefix));
};

const getPortionMassesConfig = (prefix: string, materialLabel: string, dryMassBalanceReadability: any) => {
  const massPrefix = `${prefix}.Masses`;
  const measuredUsingSubspecimenDryMassBalance = measuredUsing(
    `${prefix}.Subspecimen.Masses.MassMeasurement.Equipment.DryMassBalance`
  );

  return {
    [`${massPrefix}.WetMass`]: massConfig,
    [`${massPrefix}.DryMass`]: dryMassBalanceReadability,
    [`${massPrefix}.MassUpscaleFactor`]: { format: { nearest: 0.01 } },
    [`${massPrefix}.DryMassCombinedWithSeparationLosses`]: measuredUsingSubspecimenDryMassBalance,
    [`${massPrefix}.ExpectedDryMassCombinedWithSeparationLosses`]: massConfig,

    ...getPortionMassMeasurementConfig(massPrefix, materialLabel),
    ...getConfigForMeasuredTest(
      qestIds.moistureContent.BS_EN_1097_5.rev2008,
      `${massPrefix}.AssociatedMoistureContent`
    ),
    ...getMoistureContentPercent(`${massPrefix}.MoistureContent`),
  };
};

const getWetMassMeasurementsConfig = (prefix: string) => {
  const measuredUsingBalance = measuredUsing(`${prefix}.Equipment.Balance`);

  return {
    [`${prefix}.WetMassMeasurements.WetMass`]: derivedFrom([
      `${prefix}.WetMassMeasurements.TareMass`,
      `${prefix}.WetMassMeasurements.TareAndWetMass`,
    ]),
    [`${prefix}.WetMass`]: derivedFrom([
      `${prefix}.WetMassMeasurements.TareMass`,
      `${prefix}.WetMassMeasurements.TareAndWetMass`,
    ]),

    [`${prefix}.WetMassMeasurements.TareMass`]: measuredUsingBalance,
    [`${prefix}.WetMassMeasurements.TareAndWetMass`]: measuredUsingBalance,
  };
};

const getPortionWashingConfig = (prefix: string) => {
  const measuredUsingDryMassBalance = measuredUsing(
    `${prefix}.AssociatedWashTest.MeasuredTest.Masses.MeasuredWashedConstantDryMass.Equipment.DryMassBalance`
  );

  return mergeConfig({
    [`${prefix}.WashedDryMass`]: measuredUsingDryMassBalance,
    ...getConfigForMeasuredTest(qestIds.wash.BS_EN_933_1.Rev2012, `${prefix}.AssociatedWashTest`),
  });
};

const getDryMassBalance = (prefix: string) => (configArgs: any) => {
  const units = oPath.get(
    configArgs.worksheet,
    `${prefix}.WholePortion.Preparation.ConditionOfSampleTested`
  ) as ConditionOfSampleTested;

  if (units === ConditionOfSampleTested.PreDried) {
    return measuredUsing(`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.DryMassBalance`);
  } else if (units === ConditionOfSampleTested.OvenDry) {
    return measuredUsing(
      `${prefix}.WholePortion.Masses.MassMeasurement.MeasuredConstantDryMass.Equipment.DryMassBalance`
    );
  }

  const dryMassSource = oPath.get(configArgs.worksheet, `${prefix}.WholePortion.Masses.DryMassSource`) as DryMassSource;
  switch (dryMassSource) {
    case DryMassSource.CalculatedFromSeparationChildMasses: //to support Dry Mass in Initial separation for Annex A
      return derivedFrom([
        `${prefix}.WholePortion.Separation.CoarsePortion.Masses.MassMeasurement.MeasuredConstantDryMass.DryingTrays.DryMassReadings.DrySoilAndContainerMass`,
        `${prefix}.WholePortion.Separation.FinePortion.Masses.MassMeasurement.MeasuredConstantDryMass.DryingTrays.DryMassReadings.DrySoilAndContainerMass`,
      ]);
  }

  return massConfig;
};

const getSieveConfig = (portionSievingPath: string) => (configArgs: any) => {
  const units = oPath.get(configArgs.worksheet, `${portionSievingPath}.Equipment.SieveSetUnits`);
  return {
    format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
    uom: units === 'Metric' ? 'mm' : 'in',
  };
};

//Note that the prefix parameter should already contain the root prefix within, we just needed the root separately
const getPortionSievingConfig = (rootPrefix: string, portionSievingPath: string) =>
  mergeConfig(
    {
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Width`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Length`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Diameter`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.RepresentedDryMass`]: massConfig,
      [`${portionSievingPath}.Records.PercentagePassing`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.ExpectedIndividualPanMassRetained`]: massConfig,
      [`${portionSievingPath}.UpscaledPanMass`]: massConfig,
    },
    getMeasuredUsingConfigForPortionSieving(portionSievingPath),
    getDerivedFromMeasurementsConfigForPortionSieving(portionSievingPath),
    {
      [`${portionSievingPath}.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    }
  );

const getPortionMassMeasurementConfig = (portionPrefix: string, materialLabel: string) => {
  const prefix = `${portionPrefix}.MassMeasurement`;
  const dryMassOnly = (configArgs: IConfigFunctionArgs) => {
    const wetMassSource = oPath.get(configArgs.worksheet, `${portionPrefix}.WetMassSource`) as WetMassSource;
    const dryMassSource = oPath.get(
      configArgs.worksheet,
      `${portionPrefix}.StateAfterMassMeasurement`
    ) as DryMassSource;
    return wetMassSource !== WetMassSource.Measured && dryMassSource === DryMassSource.Measured;
  };

  const measuredUsingBalance = measuredUsing(`${prefix}.Equipment.Balance`);
  const measuredUsingDryMassBalance = measuredUsing(`${prefix}.Equipment.DryMassBalance`);

  return {
    ...mergeConfig(getMoistureContentConfig(`${prefix}.MeasuredConstantDryMass`), {
      [`${prefix}.MeasuredConstantDryMass.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
      [`${prefix}.MeasuredConstantDryMass.Equipment.DryMassBalance`]: { defaultReadability: 0.1, defaultUom: 'g' },
      [`${prefix}.MeasuredConstantDryMass.Measurements.TotalDryMass`]: { label: `${materialLabel} Dry Mass` },
    }),
    ...getWetMassMeasurementsConfig(`${prefix}`),
    [`${prefix}.TareID`]: { maxLength: 20 },
    [`${prefix}.TareMass`]: (configArgs: IConfigFunctionArgs) =>
      dryMassOnly(configArgs) ? measuredUsingDryMassBalance : measuredUsingBalance,
    [`${prefix}.TareAndWetMass`]: measuredUsingBalance,
    [`${prefix}.TareAndDryMass`]: measuredUsingDryMassBalance,

    [`${prefix}.WetMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndWetMass`]),
    [`${prefix}.DryMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndDryMass`]),

    [`${prefix}.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    [`${prefix}.Equipment.DryMassBalance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    [`${prefix}.Volume`]: { format: { nearest: 0.001 }, uom: 'L' },
    [`${prefix}.BulkDensity`]: { format: { nearest: 0.01 }, uom: 'Mg/m³' },
  };
};

export { getConfig };

export default {
  [qestIds.psdSieveAnalysis.BS_EN_933_1.Rev2012]: getConfig,
};
